import { get } from 'lodash'
import React, { useEffect } from 'react'
import { useFraudnet } from '@paypalcorp/shopping-components'

import Layout from 'components/Layout'

import PageLoadingSpinner from '../shopping/components/v2/PageLoadingSpinner'
import LogoutModal from '../shopping/components/v2/LogoutModal'
import { useStoreProfileData } from '../../hooks/useStoreProfileData'
import { StoreDetailsHeader } from '../shopping/components/v2/StoreDetailsHeader'
import {
  StoreDetailsInfoSection,
  StoreDetail
} from '../shopping/components/v2/StoreDetailsInfoSection'
import SubNavBarV3 from '../../components/SubNavBarV3'
import useBreakpoint from '../../lib/useBreakpoint'
import OfferSection from '../shopping/components/v2/OfferSection'
import BackLink from '../shopping/components/v2/BackLink'
import { setupPa } from '../../lib/track-client-side'
import { sendFptiImpression, sendFptiClick } from '../../lib/fpti-pa'
import {
  SHOPPING_STORE_DETAIL_PAGE,
  SHOPPING_STORE_DISMISS_PRESSED,
  SHOPPING_STORE_SCREEN_SHOWN
} from '../../lib/client-constants'
import { filterNullItems } from '../../lib/filterNullItems'
import { FRAUDNET_APP_ID } from '../../constants/fraudnet'

export interface StoreProfileFPTI {
  store_id: string // Honey store id
  cashback_enabled: boolean
  cashback_amt: string
  merchant_id: string
  merchant_name: string
}

const StoreProfilePage = ({ merchantId }: { merchantId: string }) => {
  const breakpoint = useBreakpoint()
  const { store, loading } = useStoreProfileData(merchantId)
  const storeProfileFPTI: StoreProfileFPTI | null = !store
    ? null
    : {
        store_id: get(store, 'storeIds[1]'), // Honey store id
        cashback_enabled: !!store.rewardsPointsLabel, // Cashback enabled indicatorof the stores
        cashback_amt: store.rewardsPointsLabel || '',
        merchant_id: get(store, 'storeIds[0]'),
        merchant_name: store.title
      }

  const createStoreDetails = (): StoreDetail[] => {
    let storeDetails: StoreDetail[] = []
    const storeDetailsSections = [
      'storeDescription',
      'freeReturn',
      'freeShipping',
      'payInFourPayments'
    ]
    storeDetailsSections.forEach(section => {
      if (get(store, section)) {
        if (section === 'storeDescription') {
          storeDetails.push({
            title: get(store, 'title') || '',
            content: get(store, section)
          })
        } else {
          storeDetails.push(get(store, section))
        }
      }
    })
    return storeDetails
  }

  const handleFptiBackLink = () => {
    sendFptiClick({
      event_name: SHOPPING_STORE_DISMISS_PRESSED,
      dismiss_method: 'back',
      ...storeProfileFPTI
    })
  }

  useEffect(() => {
    setupPa(SHOPPING_STORE_DETAIL_PAGE, SHOPPING_STORE_SCREEN_SHOWN)
  }, [])

  useEffect(() => {
    if (loading || !store) return
    sendFptiImpression({
      event_name: SHOPPING_STORE_SCREEN_SHOWN,
      cashback_amt: store.rewardsPointsLabel, // Cashback amount of the store
      cashback_enabled: !!store.rewardsPointsLabel, // Cashback enabled indicatorof the stores
      store_id: get(store, 'storeIds[1]'), // Honey store id
      merchant_id: get(store, 'storeIds[0]'),
      merchant_name: store.title
    })
  }, [loading, store])

  useFraudnet(FRAUDNET_APP_ID)

  //Feature Offer isVisible is false while we hammer out some details and clean this up
  return (
    <Layout className="store-profile-page">
      <SubNavBarV3 active="" />
      <BackLink
        isVisible={breakpoint.isMobile}
        fptiOnClick={handleFptiBackLink}
      />
      <div
        data-cy="store-profile-page"
        className="store-profile-page__container"
      >
        <StoreDetailsHeader isVisible={!loading} storeDetails={store} />
        <PageLoadingSpinner isVisible={loading} />
        <div
          className="store-profile-page__content"
          data-cy="store-profile-page__content"
        >
          <OfferSection
            isVisible={!loading}
            couponTerms={get(store, 'defaultCouponTerms', {
              title: '',
              content: ''
            })}
            deals={get(store, 'honeyDeals', [])}
            codes={filterNullItems(get(store, 'honeyCoupons', []) ?? [])}
            landingUrl={get(store, 'landingUrl')}
            fptiData={storeProfileFPTI}
          />
          <StoreDetailsInfoSection
            storeDetails={createStoreDetails()}
            fptiData={storeProfileFPTI}
          />
        </div>
      </div>
      <LogoutModal />
    </Layout>
  )
}
export default StoreProfilePage
